/**
 * CR-7750: the supplier's own "add an approval" dialog.
 *
 * Deliberately NOT a reuse of ApprovalRegisterEditDialog. That one writes the
 * live register row; this one raises a submission the customer must accept,
 * and the two differ in what they ask for:
 *
 *  - no supplier picker — a supplier submits for themselves, and the server
 *    stamps the company from the active supplier context either way;
 *  - the accreditation list is the CUSTOMER's catalogue, so it is grouped by
 *    customer and says whose entry each one is;
 *  - one certificate can be submitted to several customers at once (the
 *    fan-out below), because the schema holds one catalogue entry per
 *    customer and a supplier holds one real-world certificate.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Info } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import FileDropZone from '../../components/common/FileDropZone';
import { qualityAPI } from '../../lib/api';

/**
 * The fields amending an open submission needs.
 *
 * Narrower than `AccreditationSubmission` on purpose. What the matrix hands
 * back for a pending cell is an `ApprovalRegisterPending`, which carries every
 * field below but none of the rest of the review trail. Typing the prop to
 * what this dialog actually reads lets either shape be passed without a cast —
 * and the previous signature is why amend was never wired up: no caller had an
 * `AccreditationSubmission` to give it.
 */
export interface AmendableSubmission {
  id: number;
  accreditation: number;
  issued_date: string | null;
  expiry_date: string | null;
  no_expiry_applies: boolean;
  certificate_number: string;
  notes: string;
  has_certificate: boolean;
  certificate_file_name?: string;
}

/** The catalogue rows a supplier may claim against. */
export interface CatalogueOption {
  id: number;
  name: string;
  company: number;
  company_name: string;
  approval_group_name: string | null;
}

export interface SupplierSubmitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-select this catalogue entry (clicking an empty cell). */
  prefillAccreditationId?: number | null;
  /** An existing pending submission to amend, rather than a new one. */
  existingSubmission?: AmendableSubmission | null;
  onSaved: () => void;
}

export default function SupplierSubmitDialog({
  open,
  onOpenChange,
  prefillAccreditationId,
  existingSubmission,
  onSaved,
}: SupplierSubmitDialogProps) {
  const isAmend = Boolean(existingSubmission);

  const [catalogue, setCatalogue] = useState<CatalogueOption[]>([]);
  const [loadingCatalogue, setLoadingCatalogue] = useState(false);
  const [accreditationId, setAccreditationId] = useState<number | null>(null);
  const [issuedDate, setIssuedDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [noExpiryApplies, setNoExpiryApplies] = useState(false);
  const [certificateNumber, setCertificateNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [alsoSubmitTo, setAlsoSubmitTo] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);

  const loadCatalogue = useCallback(async () => {
    try {
      setLoadingCatalogue(true);
      const res = await qualityAPI.getAccreditations({ page_size: 500 });
      const rows = Array.isArray(res.data) ? res.data : res.data.results;
      setCatalogue(rows as unknown as CatalogueOption[]);
    } catch {
      toast.error('Could not load the approvals your customers list');
    } finally {
      setLoadingCatalogue(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    loadCatalogue();
    if (existingSubmission) {
      setAccreditationId(existingSubmission.accreditation);
      setIssuedDate(existingSubmission.issued_date ?? '');
      setExpiryDate(existingSubmission.expiry_date ?? '');
      setNoExpiryApplies(existingSubmission.no_expiry_applies);
      setCertificateNumber(existingSubmission.certificate_number);
      setNotes(existingSubmission.notes);
    } else {
      setAccreditationId(prefillAccreditationId ?? null);
      setIssuedDate('');
      setExpiryDate('');
      setNoExpiryApplies(false);
      setCertificateNumber('');
      setNotes('');
    }
    setFile(null);
    setAlsoSubmitTo([]);
  }, [open, existingSubmission, prefillAccreditationId, loadCatalogue]);

  const selected = useMemo(
    () => catalogue.find((c) => c.id === accreditationId) ?? null,
    [catalogue, accreditationId],
  );

  /**
   * The same approval in other customers' catalogues. One real certificate,
   * one upload — the server still records a submission per customer, because
   * each of them reviews their own.
   */
  const fanOutOptions = useMemo(() => {
    if (!selected) return [];
    return catalogue.filter(
      (c) => c.id !== selected.id
        && c.name.trim().toLowerCase() === selected.name.trim().toLowerCase(),
    );
  }, [catalogue, selected]);

  const buildForm = (targetId: number) => {
    const fd = new FormData();
    fd.append('accreditation', String(targetId));
    if (issuedDate) fd.append('issued_date', issuedDate);
    if (expiryDate && !noExpiryApplies) fd.append('expiry_date', expiryDate);
    fd.append('no_expiry_applies', String(noExpiryApplies));
    fd.append('certificate_number', certificateNumber);
    fd.append('notes', notes);
    if (file) fd.append('certificate_file', file);
    return fd;
  };

  const handleSubmit = async () => {
    if (!accreditationId) {
      toast.error('Choose which approval you are submitting');
      return;
    }
    if (!expiryDate && !noExpiryApplies) {
      // Same sentence the serializer returns, so one wording whichever
      // guard the user hits.
      toast.error(
        'Enter an expiry date, or tick No expiry applies to record that this accreditation does not expire.',
      );
      return;
    }

    try {
      setSaving(true);
      if (isAmend && existingSubmission) {
        await qualityAPI.updateAccreditationSubmission(
          existingSubmission.id, buildForm(accreditationId),
        );
        toast.success('Submission updated — still awaiting review');
      } else {
        const targets = [accreditationId, ...alsoSubmitTo];
        const results = await Promise.allSettled(
          targets.map((id) => qualityAPI.createAccreditationSubmission(buildForm(id))),
        );
        const failed = results.filter((r) => r.status === 'rejected').length;
        const sent = results.length - failed;
        if (sent > 0) {
          toast.success(
            sent === 1
              ? 'Submitted — your customer will review it'
              : `Submitted to ${sent} customers — each will review their own copy`,
          );
        }
        if (failed > 0) {
          toast.error(
            `${failed} submission${failed === 1 ? '' : 's'} could not be sent. `
            + 'You may already have one awaiting review for that customer.',
          );
        }
        if (sent === 0) return;
      }
      onSaved();
      onOpenChange(false);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Could not send your submission';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {isAmend ? 'Amend your submission' : 'Submit an approval'}
          </DialogTitle>
          <DialogDescription>
            Your customer reviews this before it appears on their register.
            Nothing changes for them until they accept it.
          </DialogDescription>
        </DialogHeader>

        {loadingCatalogue ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : catalogue.length === 0 ? (
          // CR-7750: a supplier can only claim what a customer has catalogued.
          // Saying so plainly stops this reading as a broken picker.
          <div className="flex gap-3 rounded-md border border-dashed p-4 text-sm text-muted-foreground">
            <Info className="h-5 w-5 shrink-0" />
            <span>
              None of your customers list any approvals yet. Approvals are set
              up by the customer — ask them to add the one you hold and it will
              appear here.
            </span>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Approval *</Label>
              <Select
                value={accreditationId ? String(accreditationId) : ''}
                onValueChange={(v) => {
                  setAccreditationId(parseInt(v, 10));
                  setAlsoSubmitTo([]);
                }}
                disabled={isAmend}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select the approval you hold" />
                </SelectTrigger>
                <SelectContent>
                  {catalogue.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.name} — {c.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {!isAmend && fanOutOptions.length > 0 && (
              <div className="space-y-2 rounded-md border p-3">
                <p className="text-sm font-medium">
                  Other customers list this approval too
                </p>
                <p className="text-xs text-muted-foreground">
                  Send the same certificate to them in one go. Each customer
                  reviews their own copy.
                </p>
                {fanOutOptions.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={alsoSubmitTo.includes(c.id)}
                      onCheckedChange={(checked) => setAlsoSubmitTo((prev) => (
                        checked ? [...prev, c.id] : prev.filter((id) => id !== c.id)
                      ))}
                    />
                    <span>{c.company_name}</span>
                  </label>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Issued Date</Label>
                <Input
                  type="date"
                  value={issuedDate}
                  onChange={(e) => setIssuedDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                {/* Required unless "No expiry applies" is ticked, and marked
                    as such — the same label treatment the customer's
                    ApprovalRegisterEditDialog uses, so the asterisk appears
                    and disappears on both sides on the same condition. */}
                <Label>Expiry Date {!noExpiryApplies && '*'}</Label>
                <Input
                  type="date"
                  value={noExpiryApplies ? '' : expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  disabled={noExpiryApplies}
                />
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={noExpiryApplies}
                onCheckedChange={(checked) => setNoExpiryApplies(Boolean(checked))}
              />
              <span>No expiry applies to this approval</span>
            </label>

            <div className="space-y-2">
              <Label>Certificate Number</Label>
              <Input
                value={certificateNumber}
                onChange={(e) => setCertificateNumber(e.target.value)}
                placeholder="e.g. ISO-2026-0199"
              />
            </div>

            <div className="space-y-2">
              <Label>Certificate</Label>
              <FileDropZone
                size="compact"
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
                hint={file
                  ? <>Selected: <span className="font-medium text-foreground">{file.name}</span></>
                  : 'PDF, image, Word or Excel'}
                onFiles={(files) => setFile(files[0])}
              />
              {isAmend && existingSubmission?.has_certificate && !file && (
                <p className="text-xs text-muted-foreground">
                  Currently attached: {existingSubmission.certificate_file_name
                    || 'certificate'}. Choose a file to replace it.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Anything the customer should know"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving || loadingCatalogue || catalogue.length === 0}
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isAmend ? 'Update submission' : 'Submit for review'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
